import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { communicationBaseRoute } from "../../../../../utils/urls";
import ViewEmailModal from "./ViewEmailModal";

function QueuedEmailTab({ facilityId }) {
  const [queuedEmails, setQueuedEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showReleaseConfirm, setShowReleaseConfirm] = useState(false);
  const [showBulkReleaseConfirm, setShowBulkReleaseConfirm] = useState(false);
  const [showReleaseAllConfirm, setShowReleaseAllConfirm] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [emailToRelease, setEmailToRelease] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [communicationStatus, setCommunicationStatus] = useState(null);
  const [sendProgress, setSendProgress] = useState(0);
  const [currentlySending, setCurrentlySending] = useState(false);
  const [totalToSend, setTotalToSend] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  // Fetch queued emails
  const fetchQueuedEmails = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/emails`,
        "GET",
      );

      // Debug logging
      console.log("Email API Response:", response);
      console.log("Email Response Data:", response.data);
      console.log("Is response.data an array?", Array.isArray(response.data));

      if (response.success) {
        // Handle the specific API response structure
        const emailData = response.data;
        if (Array.isArray(emailData)) {
          setQueuedEmails(emailData);
        } else if (emailData && typeof emailData === "object") {
          // Check for different possible response formats including emailMessages
          const emails =
            emailData.emailMessages || emailData.emails || emailData.data || [];
          console.log("Extracted emails:", emails);
          setQueuedEmails(Array.isArray(emails) ? emails : []);
        } else {
          console.log("Setting empty array - data is not array or object");
          setQueuedEmails([]);
        }
      } else {
        throw new Error(response.error || "Failed to fetch queued emails");
      }
    } catch (error) {
      console.error("Error fetching queued emails:", error);
      toastify("Error fetching queued emails: " + error.message, "error");
      setQueuedEmails([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch communication status
  const fetchCommunicationStatus = async () => {
    try {
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/status`,
        "GET",
      );

      if (response.success) {
        setCommunicationStatus(response.data);
      }
    } catch (error) {
      console.error("Error fetching communication status:", error);
    }
  };

  // Release single email
  const releaseEmail = async (emailId) => {
    try {
      // Check communication status before releasing
      if (communicationStatus && communicationStatus.emailStatus === "hold") {
        toastify(
          "Cannot release email: Communication is on hold for this facility",
          "error",
        );
        return;
      }

      setReleaseLoading(true);
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/email/${emailId}/release`,
        "POST",
      );

      if (response.success) {
        toastify("Email released successfully", "success");
        setShowReleaseConfirm(false);
        setEmailToRelease(null);
        fetchQueuedEmails(); // Refresh the list
      } else {
        throw new Error(response.error || "Failed to release email");
      }
    } catch (error) {
      toastify("Error releasing email: " + error.message, "error");
    } finally {
      setReleaseLoading(false);
    }
  };

  // Function to send emails one by one with a delay
  const timedReleaseEmails = async (emailIds) => {
    try {
      if (emailIds.length === 0) {
        toastify("No valid email IDs found", "error");
        return;
      }

      // Close any open modals before showing progress
      setShowBulkReleaseConfirm(false);

      // Reset counters and enable progress display
      setCurrentlySending(true);
      setTotalToSend(emailIds.length);
      setSendProgress(0);
      setSuccessCount(0);
      setFailedCount(0);

      for (let i = 0; i < emailIds.length; i++) {
        const emailId = emailIds[i];

        try {
          // Send email using direct endpoint
          const response = await makeRequest2(
            `${communicationBaseRoute}/${facilityId}/email/${emailId}/release`,
            "POST",
          );

          if (response.success) {
            setSuccessCount((prev) => prev + 1);
          } else {
            setFailedCount((prev) => prev + 1);
            console.error(
              "Failed to release email:",
              emailId,
              response.error || response.message,
            );
          }
        } catch (error) {
          setFailedCount((prev) => prev + 1);
          console.error("Error releasing email:", emailId, error);
        }

        // Update progress
        setSendProgress(Math.round(((i + 1) / emailIds.length) * 100));

        // Don't add delay after the last item
        if (i < emailIds.length - 1) {
          // Wait 2 seconds before next send
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      // Show final result
      toastify(
        `Email release completed: ${successCount} successful, ${failedCount} failed out of ${emailIds.length} emails`,
        successCount > 0 ? "success" : "warning",
      );

      fetchQueuedEmails(); // Refresh the list
    } finally {
      // Hide progress bar and reset
      setCurrentlySending(false);
      setTimeout(() => {
        setSendProgress(0);
        setSuccessCount(0);
        setFailedCount(0);
      }, 1000); // Delay reset to allow animation to complete
    }
  };

  // Bulk release emails - USES DIRECT ENDPOINT WITH DELAY
  const bulkReleaseEmails = async () => {
    try {
      // Check communication status before releasing
      if (communicationStatus && communicationStatus.emailStatus === "hold") {
        toastify(
          "Cannot release emails: Communication is on hold for this facility",
          "error",
        );
        return;
      }

      setReleaseLoading(true);

      if (!selectedEmails || selectedEmails.length === 0) {
        toastify("No emails selected for release", "warning");
        return;
      }

      // Extract email IDs with proper validation
      const emailIds = selectedEmails
        .filter((email) => email && email._id)
        .map((email) => email._id);

      console.log(
        "📦 Frontend: Email IDs to release via timed direct send:",
        emailIds,
      );

      if (emailIds.length === 0) {
        toastify("No valid email IDs found", "error");
        setReleaseLoading(false);
        return;
      }

      // Use the timed release method instead of bulk endpoint
      await timedReleaseEmails(emailIds);

      // Clear selection and refresh
      setSelectedEmails([]);
    } catch (error) {
      toastify("Error releasing emails: " + error.message, "error");
    } finally {
      setReleaseLoading(false);
    }
  };

  // Release all emails for facility - USES DIRECT ENDPOINT WITH DELAY
  const releaseAllEmails = async () => {
    try {
      // Check communication status before releasing
      if (communicationStatus && communicationStatus.emailStatus === "hold") {
        toastify(
          "Cannot release emails: Communication is on hold for this facility",
          "error",
        );
        return;
      }

      console.log("🚀 Frontend: Releasing all emails via timed direct send");
      setReleaseLoading(true);
      setShowReleaseAllConfirm(false); // Close modal immediately

      // First get all email messages
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/emails`,
        "GET",
      );

      if (!response.success) {
        toastify(
          "Failed to fetch queued emails: " +
            (response.error || response.message),
          "error",
        );
        return;
      }

      // Handle the specific API response structure
      let allEmails = [];
      if (Array.isArray(response.data)) {
        allEmails = response.data;
      } else if (response.data && typeof response.data === "object") {
        // Check for different possible response formats
        allEmails =
          response.data.emailMessages ||
          response.data.emails ||
          response.data.data ||
          [];
      }

      // Filter to only get pending emails
      const pendingEmails = allEmails.filter(
        (email) => email.status === "pending" || !email.status,
      );

      if (pendingEmails.length === 0) {
        toastify("No pending emails to release", "info");
        setReleaseLoading(false);
        return;
      }

      // Extract email IDs
      const emailIds = pendingEmails
        .filter((email) => email && email._id)
        .map((email) => email._id);

      // Use the timed release method
      await timedReleaseEmails(emailIds);

      setSelectedEmails([]);
    } catch (error) {
      toastify("Error releasing all emails: " + error.message, "error");
    } finally {
      setReleaseLoading(false);
    }
  };

  // Delete email from queue
  const deleteQueuedEmail = async (emailId) => {
    try {
      const response = await makeRequest2(
        `${communicationBaseRoute}/${facilityId}/email/${emailId}`,
        "DELETE",
      );

      if (response.success) {
        toastify("Email deleted from queue successfully", "success");
        fetchQueuedEmails(); // Refresh the list
      } else {
        throw new Error(response.error || "Failed to delete email");
      }
    } catch (error) {
      toastify("Error deleting email: " + error.message, "error");
    }
  };

  // Bulk delete emails
  const bulkDeleteEmails = async () => {
    try {
      setLoading(true);
      const deletePromises = selectedEmails.map((email) =>
        makeRequest2(
          `${communicationBaseRoute}/${facilityId}/email/${email._id}`,
          "DELETE",
        ),
      );

      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(
        (result) => result.status === "fulfilled" && result.value.success,
      ).length;
      const failed = results.length - successful;

      if (successful > 0) {
        toastify(
          `${successful} email(s) deleted successfully${failed > 0 ? `, ${failed} failed` : ""}`,
          "success",
        );
      }
      if (failed > 0 && successful === 0) {
        toastify(`Failed to delete ${failed} email(s)`, "error");
      }

      setSelectedEmails([]);
      fetchQueuedEmails();
    } catch (error) {
      toastify("Error deleting emails: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const confirmDelete = (email) => {
    setEmailToDelete(email);
    setShowDeleteConfirm(true);
  };

  const handleDelete = () => {
    if (emailToDelete) {
      deleteQueuedEmail(emailToDelete._id);
      setShowDeleteConfirm(false);
      setEmailToDelete(null);
    }
  };

  // Handle bulk delete confirmation
  const confirmBulkDelete = () => {
    if (selectedEmails.length > 0) {
      setShowBulkDeleteConfirm(true);
    }
  };

  const handleBulkDelete = () => {
    bulkDeleteEmails();
    setShowBulkDeleteConfirm(false);
  };

  // Handle release confirmation
  const confirmRelease = (email) => {
    setEmailToRelease(email);
    setShowReleaseConfirm(true);
  };

  const handleRelease = () => {
    if (emailToRelease) {
      releaseEmail(emailToRelease._id);
      setShowReleaseConfirm(false);
      setEmailToRelease(null);
    }
  };

  // Handle bulk release confirmation
  const confirmBulkRelease = () => {
    if (selectedEmails.length > 0) {
      setShowBulkReleaseConfirm(true);
    }
  };

  const handleBulkRelease = () => {
    bulkReleaseEmails();
    setShowBulkReleaseConfirm(false);
  };

  // Handle release all confirmation
  const confirmReleaseAll = () => {
    setShowReleaseAllConfirm(true);
  };

  const handleReleaseAll = () => {
    releaseAllEmails();
    setShowReleaseAllConfirm(false);
  };

  // Handle view email
  const handleViewEmail = (email) => {
    setSelectedEmail(email);
    setShowViewModal(true);
  };

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    fetchQueuedEmails();
  };

  // Date formatter
  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  // Subject column template
  const subjectBodyTemplate = (rowData) => {
    const subject = rowData.email?.subject || "-";
    return (
      <div className="d-flex align-items-center">
        <span className="text-truncate" style={{ maxWidth: "200px" }}>
          {subject}
        </span>
      </div>
    );
  };

  // Recipient column template
  const recipientBodyTemplate = (rowData) => {
    return rowData.email?.to || "-";
  };

  // Status column template
  const statusBodyTemplate = (rowData) => {
    const status = rowData.status || "pending";
    let severity = "warning";
    let icon = "ti ti-clock";

    switch (status) {
      case "pending":
        severity = "warning";
        icon = "ti ti-clock";
        break;
      case "failed":
        severity = "danger";
        icon = "ti ti-alert-triangle";
        break;
      case "sent":
        severity = "success";
        icon = "ti ti-check";
        break;
      default:
        severity = "info";
        icon = "ti ti-info-circle";
    }

    return (
      <div className="d-flex align-items-center gap-1">
        <i className={icon} style={{ fontSize: "12px" }}></i>
        <Badge value={status} severity={severity} />
      </div>
    );
  };

  // Actions column template
  const actionsBodyTemplate = (rowData) => {
    const isPending = rowData.status === "pending" || !rowData.status;

    return (
      <div className="d-flex gap-1">
        <Button
          icon="ti ti-eye"
          size="small"
          className="p-button-outlined p-button-info"
          onClick={() => handleViewEmail(rowData)}
          tooltip="View Email"
          tooltipOptions={{ position: "top" }}
        />
        {isPending && (
          <Button
            icon="ti ti-send"
            size="small"
            className="p-button-outlined p-button-success"
            onClick={() => confirmRelease(rowData)}
            tooltip="Release Email"
            tooltipOptions={{ position: "top" }}
            loading={releaseLoading}
          />
        )}
        <Button
          icon="ti ti-trash"
          size="small"
          className="p-button-outlined p-button-danger"
          onClick={() => confirmDelete(rowData)}
          tooltip="Delete Email"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  // Table header
  const renderHeader = () => {
    return (
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3">
          <h6 className="mb-0">
            <i className="ti ti-mail me-2"></i>
            Queued Emails ({queuedEmails.length})
          </h6>
          {queuedEmails.filter(
            (email) => email.status === "pending" || !email.status,
          ).length > 0 && (
            <Button
              icon="ti ti-send-2"
              size="small"
              className="p-button-info p-button-outlined"
              onClick={confirmReleaseAll}
              tooltip="Release All Pending"
              tooltipOptions={{ position: "top" }}
              style={{ height: "32px" }}
              loading={releaseLoading}
              label="Release All"
            />
          )}
          {selectedEmails.length > 0 && (
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-6">
                {selectedEmails.length} selected
              </span>
              <Button
                icon="ti ti-send"
                size="small"
                className="p-button-success p-button-outlined"
                onClick={confirmBulkRelease}
                tooltip="Release Selected"
                tooltipOptions={{ position: "top" }}
                style={{ height: "32px" }}
                loading={releaseLoading}
                disabled={selectedEmails.every(
                  (email) =>
                    email.status !== "pending" && email.status !== undefined,
                )}
              />
              <Button
                icon="ti ti-trash"
                size="small"
                className="p-button-danger p-button-outlined"
                onClick={confirmBulkDelete}
                tooltip="Delete Selected"
                tooltipOptions={{ position: "top" }}
                style={{ height: "32px" }}
              />
            </div>
          )}
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="p-input-icon-left">
            <i
              className="ti ti-search"
              style={{ left: "12px", color: "#6c757d" }}
            />
            <InputText
              type="search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search emails..."
              className="p-inputtext-sm"
              style={{
                paddingLeft: "35px",
                width: "250px",
                height: "32px",
              }}
            />
          </div>
          <Button
            icon={refreshing ? "ti ti-loader animate-spin" : "ti ti-refresh"}
            size="small"
            className="p-button-outlined p-button-secondary"
            onClick={handleRefresh}
            disabled={refreshing}
            tooltip="Refresh"
            tooltipOptions={{ position: "top" }}
            style={{ height: "32px", width: "32px" }}
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (facilityId) {
      fetchQueuedEmails();
      fetchCommunicationStatus();
    }
  }, [facilityId]);

  return (
    <div className="queued-email-tab">
      {currentlySending && (
        <Card className="mb-3">
          <div className="send-progress-container">
            <h6 className="mb-2">
              <i className="ti ti-send me-2"></i>
              Sending Email Messages
            </h6>
            <div className="progress mb-2" style={{ height: "20px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${sendProgress}%` }}
                aria-valuenow={sendProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {sendProgress}%
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-success">Success: {successCount}</span>
              <span className="text-danger">Failed: {failedCount}</span>
              <span className="text-primary">Total: {totalToSend}</span>
            </div>
          </div>
        </Card>
      )}
      <Card>
        <DataTable
          value={Array.isArray(queuedEmails) ? queuedEmails : []}
          loading={loading}
          header={renderHeader()}
          globalFilter={globalFilter}
          emptyMessage="No queued emails found"
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          responsiveLayout="scroll"
          className="p-datatable-sm"
          selection={selectedEmails}
          onSelectionChange={(e) => setSelectedEmails(e.value)}
          dataKey="_id"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
          <Column
            field="email.subject"
            header="Subject"
            body={subjectBodyTemplate}
            sortable
            style={{ minWidth: "200px" }}
          />
          <Column
            field="email.to"
            header="To"
            body={recipientBodyTemplate}
            sortable
            style={{ minWidth: "180px" }}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            sortable
            style={{ minWidth: "100px" }}
          />
          <Column
            field="createdAt"
            header="Queued At"
            body={(rowData) => formatDate(rowData.createdAt)}
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            header="Actions"
            body={actionsBodyTemplate}
            exportable={false}
            style={{ minWidth: "120px" }}
          />
        </DataTable>
      </Card>

      {/* View Email Modal */}
      <ViewEmailModal
        visible={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setSelectedEmail(null);
        }}
        email={selectedEmail}
        facilityId={facilityId}
        onEmailUpdated={fetchQueuedEmails}
        onEmailDeleted={fetchQueuedEmails}
      />

      {/* Release Single Email Confirmation Dialog */}
      <Dialog
        visible={showReleaseConfirm}
        style={{ width: "450px" }}
        header="Confirm Release Email"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowReleaseConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Release"
              icon="ti ti-send"
              onClick={() => emailToRelease && releaseEmail(emailToRelease._id)}
              className="p-button-success"
              loading={releaseLoading}
              disabled={communicationStatus?.emailStatus === "hold"}
            />
          </div>
        }
        onHide={() => setShowReleaseConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-send"
            style={{ fontSize: "2rem", color: "var(--green-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to release this email?
            <br />
            <strong>Subject:</strong> {emailToRelease?.email?.subject}
            <br />
            <strong>To:</strong> {emailToRelease?.email?.to}
            <br />
            <small className="text-muted">
              The email will be sent immediately if the facility communication
              status allows it.
              {communicationStatus?.emailStatus === "hold" && (
                <span className="text-danger"> (Currently on hold)</span>
              )}
            </small>
          </span>
        </div>
      </Dialog>

      {/* Release All Emails Confirmation Dialog */}
      <Dialog
        visible={showReleaseAllConfirm}
        style={{ width: "450px" }}
        header="Confirm Release All Emails"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowReleaseAllConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Release All"
              icon="ti ti-send-2"
              onClick={handleReleaseAll}
              className="p-button-info"
              loading={releaseLoading}
            />
          </div>
        }
        onHide={() => setShowReleaseAllConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-send-2"
            style={{ fontSize: "2rem", color: "var(--blue-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to release ALL pending emails for this
            facility?
            <br />
            <small className="text-muted">
              All pending emails will be processed in chunks of 10 with 2-second
              delays. Successfully sent emails will be removed from the queue,
              while failed emails will remain.
              {communicationStatus?.emailStatus === "hold" && (
                <span className="text-danger">
                  {" "}
                  (Cannot release: Communication is on hold)
                </span>
              )}
            </small>
          </span>
        </div>
      </Dialog>

      {/* Bulk Release Confirmation Dialog */}
      <Dialog
        visible={showBulkReleaseConfirm}
        style={{ width: "450px" }}
        header="Confirm Bulk Release"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowBulkReleaseConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Release All"
              icon="ti ti-send"
              onClick={() => bulkReleaseEmails()}
              className="p-button-success"
              loading={releaseLoading}
              disabled={communicationStatus?.emailStatus === "hold"}
            />
          </div>
        }
        onHide={() => setShowBulkReleaseConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-send"
            style={{ fontSize: "2rem", color: "var(--green-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to release the {selectedEmails.length}{" "}
            selected email(s)?
            <br />
            <small className="text-muted">
              Emails will be processed in chunks of 10 with 2-second delays.
              Successfully sent emails will be removed from the queue, while
              failed emails will be marked as failed.
              {communicationStatus?.emailStatus === "hold" && (
                <span className="text-danger">
                  {" "}
                  (Cannot release: Communication is on hold)
                </span>
              )}
            </small>
          </span>
        </div>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        visible={showBulkDeleteConfirm}
        style={{ width: "450px" }}
        header="Confirm Bulk Delete"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowBulkDeleteConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Delete All"
              icon="ti ti-trash"
              onClick={handleBulkDelete}
              className="p-button-danger"
              loading={loading}
            />
          </div>
        }
        onHide={() => setShowBulkDeleteConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-exclamation-triangle"
            style={{ fontSize: "2rem", color: "var(--orange-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to delete {selectedEmails.length} selected
            email(s) from the queue?
            <br />
            <small className="text-muted">This action cannot be undone.</small>
          </span>
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        visible={showDeleteConfirm}
        style={{ width: "450px" }}
        header="Confirm Delete"
        modal
        footer={
          <div>
            <Button
              label="Cancel"
              icon="ti ti-x"
              onClick={() => setShowDeleteConfirm(false)}
              className="p-button-text"
            />
            <Button
              label="Delete"
              icon="ti ti-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
          </div>
        }
        onHide={() => setShowDeleteConfirm(false)}
      >
        <div className="confirmation-content">
          <i
            className="ti ti-exclamation-triangle"
            style={{ fontSize: "2rem", color: "var(--orange-500)" }}
          />
          <span className="ms-3">
            Are you sure you want to delete this email from the queue?
            <br />
            <strong>Subject:</strong> {emailToDelete?.email?.subject}
            <br />
            <strong>To:</strong> {emailToDelete?.email?.to}
          </span>
        </div>
      </Dialog>
    </div>
  );
}

export default QueuedEmailTab;
